import type { ConstantsInboxListStruct } from "@gosaas/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, requestWithHandling } from "@/utils/api";
import { reportQueryError } from "@/utils/query-errors";

export const INBOX_QUERY_KEY = ["inbox-notifications"];

/** Poll interval for the unread badge (ms). */
export const INBOX_POLL_INTERVAL = 60_000;

export const useFetchInbox = (options?: { enabled?: boolean }) => {
	const {
		data: inbox,
		isLoading: isLoadingInbox,
		refetch: refetchInbox,
	} = useQuery({
		queryKey: INBOX_QUERY_KEY,
		queryFn: async () => {
			try {
				const response = await requestWithHandling<ConstantsInboxListStruct>(
					() =>
						apiClient.api.userNotificationsList({
							page_size: 20,
						}),
				);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to fetch notifications");
			} catch (err) {
				reportQueryError(err, {
					title: "Failed to load notifications",
					silent: true,
				});
			}
		},
		enabled: options?.enabled ?? true,
		refetchInterval: INBOX_POLL_INTERVAL,
		retry: 1,
		retryDelay: 1000,
	});

	return { inbox, isLoadingInbox, refetchInbox };
};

export const useInboxActions = () => {
	const queryClient = useQueryClient();

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });

	const markRead = async (id: string) => {
		await requestWithHandling<void>(() =>
			apiClient.api.userNotificationsReadCreate(id),
		);
		await invalidate();
	};

	const markAllRead = async () => {
		await requestWithHandling<void>(() =>
			apiClient.api.userNotificationsReadAllCreate(),
		);
		await invalidate();
	};

	const remove = async (id: string) => {
		await requestWithHandling<void>(() =>
			apiClient.api.userNotificationsDelete(id),
		);
		await invalidate();
	};

	return { markRead, markAllRead, remove };
};
